import { RIGGED_DUELIST_SOURCE_FPS } from './rigged-duelist-clips.data';

/**
 * The two pieces of arithmetic behind the rigged-duelist player, kept pure so
 * they can be asserted rather than eyeballed in a browser.
 *
 * Both are the kind of thing that fails quietly: a wrong cell offset shows a
 * neighbouring frame (which still looks like a sprite), and a wrong quantiser
 * plays the clip at the wrong speed (which still looks like animation).
 */

type FrameIndexAtTimeInput = {
  /** Seconds since playback started. */
  elapsedSeconds: number;
  /** Total frames authored for the clip, at the source sampling rate. */
  frameCount: number;
  /** Rate the reader has chosen to watch at. */
  playbackFps: number;
};

/**
 * Which authored frame should be on screen at a given moment.
 *
 * The clip's real duration is fixed by its frame count at the source rate, so
 * choosing 24 fps does not slow playback down — it shows fewer distinct frames
 * over the same span. That is the comparison the article is making, and it only
 * holds if the duration is derived from the source rate rather than the
 * playback rate.
 */
export function resolveFrameIndexAtTime({
  elapsedSeconds,
  frameCount,
  playbackFps,
}: FrameIndexAtTimeInput): number {
  if (frameCount <= 1) {
    return 0;
  }

  const durationSeconds = frameCount / RIGGED_DUELIST_SOURCE_FPS;
  // Modulo twice so a negative elapsed time (a clock adjustment mid-clip)
  // wraps forward rather than producing a negative frame.
  const positionSeconds =
    ((elapsedSeconds % durationSeconds) + durationSeconds) % durationSeconds;
  const quantizedSeconds =
    Math.floor(positionSeconds * playbackFps) / playbackFps;

  return Math.min(
    frameCount - 1,
    Math.round(quantizedSeconds * RIGGED_DUELIST_SOURCE_FPS),
  );
}

type CellOffsetInput = {
  frameIndex: number;
  /** Columns in the sheet grid. */
  columns: number;
  /** Rendered size of one cell, in CSS pixels. */
  viewerSize: number;
};

/**
 * The `background-position` for a frame, in CSS pixels. Offsets are negative
 * because the sheet is moved left and up behind a fixed window.
 */
export function resolveCellOffset({
  frameIndex,
  columns,
  viewerSize,
}: CellOffsetInput): { x: number; y: number } {
  const column = frameIndex % columns;
  const row = Math.floor(frameIndex / columns);

  // Negated explicitly rather than as `-column * viewerSize`, which yields the
  // negative zero that CSS renders as `-0px`.
  return {
    x: column === 0 ? 0 : -(column * viewerSize),
    y: row === 0 ? 0 : -(row * viewerSize),
  };
}

/** The offset above, formatted for `style.backgroundPosition`. */
export function formatCellBackgroundPosition(input: CellOffsetInput): string {
  const { x, y } = resolveCellOffset(input);
  return `${x}px ${y}px`;
}
